<template>
    <div v-if="value" :class="classList">
        <v-layout :align-center="!vertical" justify-start :column="vertical">
            <v-flex :shrink="!vertical" class="d-flex align-center" :class="{ 'justify-center': vertical }">
                <template v-if="value.format === 'IMAGE' && value.src">
                    <img
                        class="media-upload-preview__image"
                        :src="value.src"
                        :alt="value.alt"
                        @error="$emit('image-error', $event)"
                    />
                </template>
                <template v-else-if="value.format === 'VIDEO' && value.video">
                    <app-video class="media-upload-preview__video" :options="value.video" />
                </template>
                <v-icon v-else-if="value.icon" class="media-upload-preview__icon" :size="vertical ? '5rem' : '2rem'">
                    {{ getIcon(value.icon, 'fad') }}
                </v-icon>
            </v-flex>
            <v-flex v-if="value.name || value.size" :grow="!vertical" class="media-upload-preview__text">
                <p v-if="value.name" class="mb-0">{{ value.name }}</p>
                <p v-if="value.size" class="mb-0 caption text--secondary">{{ value.size }}</p>
            </v-flex>
            <v-flex class="d-flex media-upload-preview__actions">
                <app-button
                    v-if="!hideRemoveButton"
                    class="media-upload-preview__remove"
                    :icon="getIcon('close')"
                    :text="vertical ? $t('remove') : null"
                    :disabled="loading"
                    :secondary="vertical"
                    @click.stop="$emit('remove', value)"
                />
                <div v-if="vertical && !hideRemoveButton && !hideDownloadButton" class="mx-2" />
                <app-button
                    v-if="!hideDownloadButton"
                    class="media-upload-preview__download"
                    :icon="getIcon('download', 'fad')"
                    :text="vertical ? $t('download') : null"
                    :disabled="loading"
                    @click.stop="$emit('download', value)"
                />
            </v-flex>
        </v-layout>
        <v-progress-linear
            v-if="!hideUploadProgress && !value.uploaded"
            :indeterminate="loading"
            color="primaryBackground"
            class="media-upload-preview__progress"
        />
    </div>
</template>

<script>
export default {
    name: 'AppMediaUploadPreview',
    props: {
        value: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        vertical: {
            type: Boolean,
            default: false
        },
        hideRemoveButton: {
            type: Boolean,
            default: false
        },
        hideDownloadButton: {
            type: Boolean,
            default: false
        },
        hideUploadProgress: {
            type: Boolean,
            default: false
        },
        background: {
            type: String,
            default: 'background darken2'
        }
    },
    computed: {
        backgroundColour() {
            if (!this.background) {
                return '';
            }

            return this.background.split(' ')[0];
        },
        backgroundShade() {
            if (!this.background) {
                return '';
            }

            const split = this.background.split(' ');

            if (split.length !== 2) {
                return '';
            }

            const shade = split[1];

            return shade.replace(/(\w+)(\d+)/, (match, word, digits) => {
                return word + '-' + digits;
            });
        },
        classList() {
            let classList = ['media-upload-preview'];

            if (this.backgroundColour) {
                classList.push(this.backgroundColour);
            }

            if (this.backgroundShade) {
                classList.push(this.backgroundShade);
            }

            if (this.vertical) {
                classList.push('media-upload-preview--vertical');
            }

            return classList.join(' ');
        }
    }
};
</script>

<style lang="scss" scoped>
.media-upload-preview {
    position: relative;
    overflow: hidden;
    margin-top: 1rem;
    border-radius: 4px;
    &__image {
        display: block;
        margin: 0.8rem 1.2rem;
        height: 2.4rem;
        width: auto;
        border-radius: 4px;
    }
    &__icon {
        margin: 0.8rem 1.2rem;
    }
    &__progress {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
    }
    &__actions {
        position: absolute;
        top: 50%;
        right: 0;
        transition: all 0.15s ease-in-out;
        transform: translateY(-50%) translateX(100%);
        opacity: 0;
        visibility: hidden;
        padding: 0.5rem;
        z-index: 1;
    }

    &:hover {
        .media-upload-preview__actions {
            transform: translateY(-50%) translateX(0%);
            opacity: 1;
            visibility: visible;
        }
    }

    &--vertical {
        margin-top: 0;

        .media-upload-preview__image {
            width: auto;
            height: auto;
            max-height: 20rem;
            max-width: calc(100% - 1rem);
            margin: 0 auto 1rem auto;
            border: 1px solid var(--v-background-darken2);
        }
        .media-upload-preview__video {
            margin-bottom: 1rem;
        }
        .media-upload-preview__icon {
            margin: 0 auto 1rem auto;
            width: fit-content;
        }

        .media-upload-preview__text {
            text-align: center;
            max-width: 20rem;
            margin: 0 auto 1rem auto;
        }

        .media-upload-preview__actions {
            position: static;
            top: auto;
            right: auto;
            opacity: 1;
            visibility: visible;
            transform: none;
            margin: 0 auto;
        }

        &:hover {
            .media-upload-preview__actions {
                transform: none;
                opacity: 1;
                visibility: visible;
            }
        }
    }
}
</style>
